if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.handleSuccessMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              // Shopify returned an error status — but the item may have been added
              // (e.g., last item in stock gets added but response is 422).
              // Check the cart to see if the variant is actually in there.
              return this.handlePossibleCappedAdd(response, formData);
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            this.handleSuccess(response, formData);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleSuccess(response, formData) {
        const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
        if (!this.error)
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          }).then(() => {
            CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
          });
        this.error = false;
        const quickAddModal = this.closest('quick-add-modal');
        if (quickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(response);
                });
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else {
          CartPerformance.measure("add:paint-updated-sections", () => {
            this.cart.renderContents(response);
          });
        }
      }

      handlePossibleCappedAdd(response, formData) {
        const variantId = formData.get('id');

        // Fetch the current cart to check if the variant was actually added
        return fetch(`${routes.cart_url}.js`, { headers: { 'Accept': 'application/json' } })
          .then((res) => res.json())
          .then((cartData) => {
            const itemInCart = cartData.items.find(
              (item) => item.variant_id.toString() === variantId.toString()
            );

            if (itemInCart) {
              // Item IS in cart — this was a capped add (last in stock), not a failure.
              // Show success confirmation, then mark variant as sold out.
              this.handleSuccessMessage('Added to cart');
              // Set error to true so the finally block doesn't re-enable the submit button —
              // we're about to disable it permanently because this variant is now sold out.
              this.error = true;

              // Publish cartUpdate so other components (variant picker, qty rules) refresh
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: cartData,
              });

              // Re-fetch sections so the cart drawer/notification has fresh HTML
              if (this.cart) {
                const sectionIds = this.cart.getSectionsToRender().map((section) => section.id);

                return fetch(`${routes.cart_url}?sections=${sectionIds.join(',')}`, {
                  headers: { 'Accept': 'application/json' },
                })
                  .then((res) => res.json())
                  .then((freshCart) => {
                    // Build a response shape that renderContents expects
                    const cartResponse = {
                      sections: freshCart.sections || freshCart,
                      id: variantId,
                      key: itemInCart.key,
                      item_count: cartData.item_count,
                    };

                    this.cart.renderContents(cartResponse);

                    // Disable the submit button to indicate this variant is now sold out
                    this.submitButton.setAttribute('aria-disabled', true);
                    this.submitButton.setAttribute('disabled', 'disabled');
                    this.submitButtonText.textContent = window.variantStrings.soldOut;
                  });
              }

              // No cart drawer/notification — just disable the button
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.setAttribute('disabled', 'disabled');
              this.submitButtonText.textContent = window.variantStrings.soldOut;
            } else {
              // Item is NOT in cart — this is a true failure
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      handleSuccessMessage(message = false) {
        this.successMessageWrapper =
          this.successMessageWrapper || this.querySelector('.product-form__success-message-wrapper');
        if (!this.successMessageWrapper) return;
        this.successMessage =
          this.successMessage || this.successMessageWrapper.querySelector('.product-form__success-message');

        this.successMessageWrapper.toggleAttribute('hidden', !message);

        if (message) {
          this.successMessage.textContent = message;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
